import type { FC, ReactNode } from 'react'

import { Mail } from 'lucide-react'
import { useI18n } from '@/i18n/context'
import { cn } from '@/lib/utils'

const GoogleIcon: FC = (): ReactNode => (
    <svg viewBox='0 0 48 48' className='size-5' aria-hidden='true'>
        <path fill='#FFC107' d='M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z' />
        <path fill='#FF3D00' d='M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z' />
        <path fill='#4CAF50' d='M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z' />
        <path fill='#1976D2' d='M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z' />
    </svg>
)

const FacebookIcon: FC = (): ReactNode => (
    <svg viewBox='0 0 24 24' className='size-5' aria-hidden='true'>
        <path fill='currentColor' d='M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z' />
    </svg>
)

const AppleIcon: FC = (): ReactNode => (
    <svg viewBox='0 0 24 24' className='size-5' aria-hidden='true'>
        <path fill='currentColor' d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' />
    </svg>
)

const base = 'flex h-13 w-full items-center justify-center gap-3 rounded-2xl px-5 text-[15px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-[#101c18]'

interface SocialButtonsProps {
    next: string
    showEmail?: boolean
    emailLabel?: string
    emailActive?: boolean
    onEmail?: () => void
}

export const SocialButtons: FC<SocialButtonsProps> = ({ next, showEmail = true, emailLabel, emailActive, onEmail }): ReactNode => {
    const { m } = useI18n()
    const query = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''

    return (
        <div className='grid gap-3'>
            <a href={`/api/auth/oauth/google${query}`} className={cn(base, 'bg-white text-[#1f1f1f] hover:bg-[#f2f2f2]')}>
                <GoogleIcon />
                {m.auth.google}
            </a>

            <a href={`/api/auth/oauth/facebook${query}`} className={cn(base, 'bg-[#1877F2] text-white hover:bg-[#0f63d6]')}>
                <FacebookIcon />
                {m.auth.facebook}
            </a>

            <a href={`/api/auth/oauth/apple${query}`} className={cn(base, 'border border-white/25 bg-black text-white hover:bg-[#151515]')}>
                <AppleIcon />
                {m.auth.apple}
            </a>

            {showEmail && (
                <button
                    type='button'
                    onClick={onEmail}
                    aria-expanded={emailActive}
                    className={cn(base, 'border border-gold/45 text-gold-bright hover:bg-gold/10', emailActive && 'bg-gold/12 border-gold/80')}
                >
                    <Mail className='size-5' aria-hidden='true' />
                    {emailLabel ?? m.auth.emailRegister}
                </button>
            )}
        </div>
    )
}
